<template>
  <div class="therapy-page specialist-page">
    <PageHeader icon="fas fa-walking"
      ><template #title>Medhop Therapy</template
      ><template #subtitle
        >Kelola episode, SOAP, outcome, latihan rumah, kuota sesi, dan discharge
        dalam satu alur.</template
      ><template #actions
        ><button v-if="canManage" class="btn btn-light mr-2" @click="managePackages">
          <i class="fas fa-box-open mr-2"></i>Kelola paket
        </button><button v-if="canManage" class="btn specialist-primary-action" @click="openNewCase">
          <i class="fas fa-plus mr-2"></i>Episode baru
        </button></template
      ></PageHeader
    >

    <b-alert
      v-if="errorMessage"
      show
      dismissible
      variant="danger"
      @dismissed="errorMessage = ''"
      >{{ errorMessage }}</b-alert
    >
    <section class="kpis">
      <article>
        <small>Episode aktif</small><strong>{{ dashboard.active || 0 }}</strong>
      </article>
      <article>
        <small>Sesi hari ini</small
        ><strong>{{ dashboard.sessionsToday || 0 }}</strong>
      </article>
      <article class="warning">
        <small>Sisa ≤ 2 sesi</small
        ><strong>{{ dashboard.lowBalance || 0 }}</strong>
      </article>
      <article>
        <small>Final bulan ini</small
        ><strong>{{ dashboard.completedThisMonth || 0 }}</strong>
      </article>
    </section>

    <section class="therapy-filters">
      <div class="therapy-search">
        <i class="fas fa-search"></i
        ><input
          v-model="filters.search"
          placeholder="Cari pasien atau nomor episode"
          @input="debouncedLoad"
        />
      </div>
      <select v-model="filters.status" @change="loadCases">
        <option value="">Semua status</option>
        <option value="planned">Direncanakan</option>
        <option value="active">Aktif</option>
        <option value="on_hold">Ditunda</option>
        <option value="completed">Selesai</option>
      </select>
    </section>
    <section class="layout">
      <aside class="case-list">
        <button
          v-for="item in cases"
          :key="item.id"
          type="button"
          :class="{ active: detail && detail.id === item.id }"
          @click="openCase(item.id)"
        >
          <span>{{ item.case_number }}</span
          ><strong>{{ item.patient_name }}</strong
          ><small
            >{{ statusLabel(item.status) }} · {{ item.completed_sessions }}/{{
              item.authorized_sessions
            }}
            sesi</small
          >
        </button>
        <p v-if="!cases.length">Belum ada episode terapi.</p>
      </aside>

      <main v-if="detail" class="case-detail">
        <div class="case-heading">
          <div>
            <span>{{ detail.case_number }}</span>
            <h2>{{ detail.patient_name }}</h2>
            <p>{{ detail.physio_diagnosis }}</p>
          </div>
          <div>
            <b class="status">{{ statusLabel(detail.status) }}</b
            ><button
              v-if="canManage && detail.package_resource_id && !detail.patient_package_id && !detail.billing"
              class="btn btn-light-primary"
              @click="sendToCashier"
            >
              <i class="fas fa-cash-register mr-1"></i>Tagihkan
            </button
            ><button
              v-if="detail.billing"
              class="btn btn-light-primary"
              @click="openInvoice"
            >
              <i class="fas fa-receipt mr-1"></i>Lihat invoice
            </button
            ><button
              v-if="canManage && detail.status === 'planned'"
              class="btn btn-primary"
              @click="transition('active')"
            >
              Aktifkan</button
            ><button
              v-if="canManage && detail.status === 'active'"
              class="btn btn-light-warning"
              @click="transition('on_hold')"
            >
              Tunda</button
            ><button
              v-if="canManage && detail.status === 'on_hold'"
              class="btn btn-primary"
              @click="transition('active')"
            >
              Lanjutkan</button
            ><button
              v-if="canManage && ['active', 'on_hold'].includes(detail.status)"
              class="btn btn-success"
              @click="discharge"
            >
              Discharge
            </button>
          </div>
        </div>
        <div class="financial-strip" :class="detail.financial_status.code">
          <div>
            <small>Status pembayaran</small>
            <strong>{{ detail.financial_status.label }}</strong>
          </div>
          <div v-if="detail.patient_package">
            <small>Saldo paket pasien</small>
            <strong>{{ detail.patient_package.sessions_remaining }} dari {{ detail.patient_package.sessions_total }} sesi</strong>
            <span v-for="item in detail.patient_package.items" :key="item.id" class="service-balance">{{ item.service_name }}: {{ item.sessions_remaining }}/{{ item.sessions_total }}</span>
          </div>
          <div v-if="detail.billing">
            <small>Invoice</small>
            <strong>{{ detail.billing.invoice_number }}</strong>
          </div>
        </div>
        <div class="quota">
          <div>
            <small>Kuota sesi</small
            ><strong
              >{{ detail.completed_sessions }} terpakai ·
              {{ detail.remaining_sessions }} tersisa</strong
            >
          </div>
          <div class="bar"><i :style="{ width: quotaPercent + '%' }"></i></div>
          <span v-if="detail.remaining_sessions <= 2"
            >Perlu tindak lanjut paket sesi</span
          >
        </div>
        <WorkspaceTabs
          :value="activeTab"
          :items="tabs"
          aria-label="Navigasi episode terapi"
          @change="activeTab = $event"
        />

        <section v-if="activeTab === 'overview'" class="overview grid">
          <article>
            <small>Therapist utama</small
            ><strong>{{
              detail.primary_therapist_name || "Belum ditentukan"
            }}</strong>
          </article>
          <article>
            <small>Periode</small
            ><strong
              >{{ date(detail.started_at) }} —
              {{ date(detail.target_end_at) }}</strong
            >
          </article>
          <article class="wide">
            <small>Tujuan terapi</small>
            <ul>
              <li v-for="goal in detail.goals" :key="String(goal)">
                {{
                  typeof goal === "string"
                    ? goal
                    : goal.description || goal.goal
                }}
              </li>
            </ul>
          </article>
          <article class="wide">
            <small>Profil fungsi (ICF)</small>
            <p>{{ icfSummary }}</p>
          </article>
          <article class="wide">
            <small>Perhatian / kontraindikasi</small>
            <p>{{ detail.precautions || "-" }}</p>
          </article>
        </section>

        <section v-if="activeTab === 'sessions'" class="records">
          <div class="section-title">
            <h3>Dokumentasi sesi</h3>
            <button
              v-if="canManage && detail.status === 'active'"
              class="btn btn-primary"
              @click="openSession"
            >
              Catat sesi
            </button>
          </div>
          <article v-for="session in detail.sessions" :key="session.id">
            <div>
              <b>Sesi {{ session.session_number }}</b
              ><span
                >{{ dateTime(session.occurred_at) }} ·
                {{ session.therapist_name || "Therapist" }}</span
              >
            </div>
            <strong>{{
              session.status === "final"
                ? "Final & terkunci"
                : session.status === "void"
                ? "Dibatalkan"
                : "Draft"
            }}</strong>
            <small v-if="session.service_name" class="session-service">{{ session.service_name }}</small>
            <p><b>S:</b> {{ session.subjective }}</p>
            <p><b>O:</b> {{ session.objective }}</p>
            <p><b>A:</b> {{ session.assessment }}</p>
            <p><b>P:</b> {{ session.plan }}</p>
            <small v-if="session.pain_before !== null"
              >Nyeri {{ session.pain_before }} →
              {{
                session.pain_after === null ? "-" : session.pain_after
              }}</small
            ><button
              v-if="canManage && session.status === 'draft'"
              class="btn btn-sm btn-success"
              @click="finalizeSession(session)"
            >
              Finalisasi sesi
            </button>
          </article>
        </section>

        <section v-if="activeTab === 'outcomes'" class="records">
          <div class="section-title">
            <h3>Outcome terstruktur</h3>
            <button
              v-if="canManage && ['planned', 'active', 'on_hold'].includes(detail.status)"
              class="btn btn-primary"
              @click="$bvModal.show('therapy-outcome')"
            >
              Tambah outcome
            </button>
          </div>
          <article v-for="outcome in detail.outcomes" :key="outcome.id">
            <div>
              <b>{{ outcome.instrument_name }}</b
              ><span
                >{{ outcome.phase }} · {{ dateTime(outcome.measured_at) }}</span
              >
            </div>
            <strong
              >{{ outcome.score
              }}{{
                outcome.maximum_score ? ` / ${outcome.maximum_score}` : ""
              }}
              {{ outcome.unit || "" }}</strong
            >
            <p>{{ outcome.interpretation || "-" }}</p>
          </article>
        </section>

        <section v-if="activeTab === 'home'" class="records">
          <div class="section-title">
            <h3>Home exercise program</h3>
            <button
              v-if="canManage && ['planned', 'active', 'on_hold'].includes(detail.status)"
              class="btn btn-primary"
              @click="$bvModal.show('therapy-home')"
            >
              Terbitkan versi baru
            </button>
          </div>
          <article v-for="hep in detail.home_programs" :key="hep.id">
            <div>
              <b>Program v{{ hep.version }}</b
              ><span>{{ date(hep.issued_at) }} · {{ hep.status }}</span>
            </div>
            <ul>
              <li v-for="exercise in hep.exercises" :key="String(exercise)">
                {{
                  typeof exercise === "string"
                    ? exercise
                    : exercise.name || exercise.exercise
                }}
              </li>
            </ul>
            <p>{{ hep.instructions }}</p>
          </article>
        </section>
      </main>
      <main v-else class="empty specialist-empty">
        <i class="fas fa-walking"></i>
        <h3>Pilih episode terapi</h3>
        <p>Atau buat episode baru untuk pasien.</p>
      </main>
    </section>

    <b-modal id="therapy-case" title="Episode terapi baru" size="lg" hide-footer
      ><div class="form-grid">
        <div class="wide">
          <PatientPicker
            :value="casePatient"
            label="Cari pasien"
            @select="selectPatient"
            @clear="selectPatient(null)"
          />
        </div>
        <label
          >Mulai<input
            v-model="caseForm.started_at"
            type="date"
            class="form-control" /></label
        ><label
          >Target selesai<input
            v-model="caseForm.target_end_at"
            type="date"
            class="form-control" /></label
        ><label class="wide"
          >Diagnosis fisioterapi<textarea
            v-model="caseForm.physio_diagnosis"
            class="form-control"
          ></textarea></label
        ><label
          >Saldo paket pasien<select
            v-model="caseForm.patient_package_id"
            class="form-control"
            :disabled="!caseForm.patient_id || !patientPackages.length"
            @change="applyPatientPackage"
          ><option :value="null">Tidak memakai saldo lama</option><option
            v-for="item in patientPackages"
            :key="item.id"
            :value="item.id"
          >{{ patientPackageName(item) }} · {{ item.sessions_remaining }} sesi tersisa</option></select></label
        ><label
          >Paket sesi<select
            v-model="caseForm.package_resource_id"
            class="form-control"
            :disabled="!!caseForm.patient_package_id"
            @change="applyPackage"
          ><option :value="null">Tanpa paket terintegrasi</option><option
            v-for="item in therapyPackages"
            :key="item.id"
            :value="item.id"
          >{{ item.name }} · {{ packageSessions(item) }} sesi · {{ money(packagePrice(item)) }}</option></select></label
        ><label
          >Kuota sesi<input
            v-model.number="caseForm.authorized_sessions"
            type="number"
            min="1"
            :readonly="!!caseForm.package_resource_id"
            class="form-control" /></label
        ><label class="wide"
          >Tujuan (satu per baris)<textarea
            v-model="caseForm.goalsText"
            class="form-control"
          ></textarea></label
        ><label class="wide"
          >Rencana intervensi (satu per baris)<textarea
            v-model="caseForm.interventionsText"
            class="form-control"
          ></textarea>
        </label>
      </div>
      <button v-if="canManage" class="btn btn-primary mt-3" @click="saveCase">
        Simpan episode
      </button></b-modal
    >

    <b-modal
      id="therapy-session"
      title="Catat SOAP sesi terapi"
      size="lg"
      hide-footer
      ><div class="form-grid">
        <label
          >Waktu sesi<input
            v-model="sessionForm.occurred_at"
            type="datetime-local"
            class="form-control" /></label
        ><label
          >Durasi (menit)<input
            v-model.number="sessionForm.duration_minutes"
            type="number"
            class="form-control" /></label
        ><label v-if="sessionServices.length"
          >Layanan paket<select
            v-model="sessionForm.payment_category_id"
            class="form-control"
            @change="applySessionService"
          ><option :value="null">Pilih layanan</option><option
            v-for="item in sessionServices"
            :key="item.payment_category_id"
            :value="item.payment_category_id"
            :disabled="Number(item.sessions_remaining) <= 0"
          >{{ item.service_name }} · {{ item.sessions_remaining }} sesi tersisa</option></select></label
        ><label
          >Nyeri sebelum<input
            v-model.number="sessionForm.pain_before"
            type="number"
            min="0"
            max="10"
            class="form-control" /></label
        ><label
          >Nyeri sesudah<input
            v-model.number="sessionForm.pain_after"
            type="number"
            min="0"
            max="10"
            class="form-control" /></label
        ><label class="wide"
          >Subjective<textarea
            v-model="sessionForm.subjective"
            class="form-control"
          ></textarea></label
        ><label class="wide"
          >Objective<textarea
            v-model="sessionForm.objective"
            class="form-control"
          ></textarea></label
        ><label class="wide"
          >Assessment<textarea
            v-model="sessionForm.assessment"
            class="form-control"
          ></textarea></label
        ><label class="wide"
          >Plan<textarea
            v-model="sessionForm.plan"
            class="form-control"
          ></textarea></label
        ><label class="wide"
          >Intervensi (satu per baris)<textarea
            v-model="sessionForm.interventionsText"
            class="form-control"
          ></textarea>
        </label>
      </div>
      <button v-if="canManage" class="btn btn-primary mt-3" @click="saveSession">
        Simpan draft sesi
      </button></b-modal
    >

    <b-modal id="therapy-outcome" title="Tambah outcome" hide-footer
      ><div class="form-grid">
        <label
          >Kode instrumen<input
            v-model="outcomeForm.instrument_code"
            class="form-control"
            placeholder="NPRS, TUG, LEFS" /></label
        ><label
          >Nama instrumen<input
            v-model="outcomeForm.instrument_name"
            class="form-control" /></label
        ><label
          >Skor<input
            v-model.number="outcomeForm.score"
            type="number"
            step="any"
            class="form-control" /></label
        ><label
          >Skor maksimum<input
            v-model.number="outcomeForm.maximum_score"
            type="number"
            step="any"
            class="form-control" /></label
        ><label
          >Fase<select v-model="outcomeForm.phase" class="form-control">
            <option value="baseline">Baseline</option>
            <option value="progress">Progress</option>
            <option value="discharge">Discharge</option>
            <option value="follow_up">Follow-up</option>
          </select></label
        ><label class="wide"
          >Interpretasi<textarea
            v-model="outcomeForm.interpretation"
            class="form-control"
          ></textarea>
        </label>
      </div>
      <button v-if="canManage" class="btn btn-primary mt-3" @click="saveOutcome">
        Simpan outcome
      </button></b-modal
    >

    <b-modal id="therapy-home" title="Home exercise program" hide-footer
      ><label
        >Latihan (satu per baris)<textarea
          v-model="homeForm.exercisesText"
          rows="5"
          class="form-control"
        ></textarea></label
      ><label class="mt-3"
        >Instruksi<textarea
          v-model="homeForm.instructions"
          class="form-control"
        ></textarea></label
      ><label class="mt-3"
        >Tanggal review<input
          v-model="homeForm.review_at"
          type="date"
          class="form-control" /></label
      ><button v-if="canManage" class="btn btn-primary mt-3" @click="saveHome">
        Terbitkan program
      </button></b-modal
    >

    <b-modal id="therapy-discharge" title="Tutup episode terapi" hide-footer
      ><label
        >Hasil terapi<select
          v-model="dischargeForm.discharge_outcome"
          class="form-control"
        >
          <option value="goals_met">Tujuan tercapai</option>
          <option value="partially_met">Tujuan tercapai sebagian</option>
          <option value="not_met">Tujuan belum tercapai</option>
          <option value="referred">Dirujuk</option>
          <option value="patient_choice">Atas pilihan pasien</option>
        </select></label
      ><label class="mt-3"
        >Ringkasan discharge<textarea
          v-model.trim="dischargeForm.discharge_summary"
          rows="5"
          class="form-control"
          placeholder="Kondisi akhir, progres, edukasi, dan rencana tindak lanjut"
        ></textarea></label
      ><p class="text-muted mb-0">
        Setelah ditutup, episode tidak dapat menerima sesi, outcome, atau program
        latihan baru.
      </p>
      <button
        v-if="canManage"
        class="btn btn-success mt-3"
        :disabled="!dischargeForm.discharge_summary"
        @click="saveDischarge"
      >
        Konfirmasi discharge
      </button></b-modal
    >
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service.js";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import WorkspaceTabs from "@/component/general/WorkspaceTabs.vue";
import PatientPicker from "@/component/general/PatientPicker.vue";
export default {
  name: "TherapyWorkspace",
  components: { WorkspaceTabs, PatientPicker },
  data: () => ({
    dashboard: {},
    cases: [],
    detail: null,
    activeTab: "overview",
    filters: { search: "", status: "", page_size: 100 },
    timer: null,
    errorMessage: "",
    caseForm: {},
    sessionForm: {},
    outcomeForm: {},
    homeForm: {},
    dischargeForm: {},
    therapyPackages: [],
    patientPackages: [],
    loadingDetail: false,
  }),
  computed: {
    canManage() {
      try {
        return (JSON.parse(localStorage.getItem("access_right_display") || "[]") || [])
          .map(String)
          .includes("1202");
      } catch (error) {
        return false;
      }
    },
    casePatient() {
      return this.caseForm.patient_id
        ? { id: this.caseForm.patient_id, name: this.caseForm.patient_name }
        : null;
    },
    quotaPercent() {
      return this.detail
        ? Math.min(
            100,
            Math.round(
              (100 * this.detail.completed_sessions) /
                Math.max(1, this.detail.authorized_sessions)
            )
          )
        : 0;
    },
    tabs() {
      return [
        { key: "overview", label: "Episode", icon: "fas fa-folder-open" },
        {
          key: "sessions",
          label: "Sesi",
          icon: "fas fa-notes-medical",
          count: this.detail.sessions.length,
        },
        {
          key: "outcomes",
          label: "Outcome",
          icon: "fas fa-chart-line",
          count: this.detail.outcomes.length,
        },
        {
          key: "home",
          label: "Latihan rumah",
          icon: "fas fa-home",
          count: this.detail.home_programs.length,
        },
      ];
    },
    icfSummary() {
      const x = this.detail.icf_profile || {};
      return Object.values(x).filter(Boolean).join(" · ") || "Belum dilengkapi";
    },
    sessionServices() {
      if (!this.detail) return [];
      if (this.detail.patient_package && Array.isArray(this.detail.patient_package.items) && this.detail.patient_package.items.length)
        return this.detail.patient_package.items;
      const payload = (this.detail.package_snapshot || {}).operational_payload || {};
      const items = Array.isArray(payload.service_items)
        ? payload.service_items
        : payload.payment_category_id
        ? [{ payment_category_id: payload.payment_category_id, sessions: payload.sessions || 1, duration_minutes: payload.session_duration_minutes }]
        : [];
      return items.map((item) => ({
        ...item,
        service_name: item.service_name || `Layanan #${item.payment_category_id}`,
        sessions_remaining: item.sessions,
      }));
    },
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Operasional Klinis" },
      { title: "Medhop Therapy" },
    ]);
    await Promise.all([this.loadDashboard(), this.loadCases(), this.loadPackages()]);
  },
  methods: {
    items(p) {
      if (Array.isArray(p.data)) return p.data;
      if (p.data && Array.isArray(p.data.data)) return p.data.data;
      return [];
    },
    async loadDashboard() {
      try {
        const r = await ApiService.get("therapy/dashboard");
        this.dashboard = r.data.data || {};
      } catch (e) {
        this.fail(e);
      }
    },
    async loadCases() {
      try {
        const r = await ApiService.query("therapy/cases", {
          params: this.filters,
        });
        this.cases = this.items(r.data);
        const requestedId = Number(this.$route.query.case_id || 0);
        const requested = requestedId && this.cases.find(item => Number(item.id) === requestedId);
        if ((!this.detail || requested) && this.cases.length)
          this.openCase(requested ? requested.id : this.cases[0].id);
      } catch (e) {
        this.fail(e);
      }
    },
    async loadPackages() {
      try {
        const r = await ApiService.query("vertical-resources", {
          params: {
            resource_type: "therapy_session_package",
            status: "active",
            available_now: 1,
            page_size: 100,
          },
        });
        this.therapyPackages = this.items(r.data);
      } catch (e) {
        this.therapyPackages = [];
      }
    },
    debouncedLoad() {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.loadCases, 250);
    },
    async openCase(id) {
      if (this.loadingDetail) return;
      this.loadingDetail = true;
      try {
        const r = await ApiService.get(`therapy/cases/${id}`);
        this.detail = r.data.data;
        this.activeTab = "overview";
      } catch (e) {
        this.fail(e);
      } finally {
        this.loadingDetail = false;
      }
    },
    openNewCase() {
      this.caseForm = {
        patient_id: null,
        patient_name: "",
        started_at: this.today(),
        target_end_at: "",
        physio_diagnosis: "",
        authorized_sessions: 8,
        package_resource_id: null,
        patient_package_id: null,
        goalsText: "",
        interventionsText: "",
      };
      this.$bvModal.show("therapy-case");
    },
    selectPatient(p) {
      this.caseForm.patient_id = p ? p.id : null;
      this.caseForm.patient_name = p ? p.name : "";
      this.caseForm.patient_package_id = null;
      this.patientPackages = [];
      if (p) this.loadPatientPackages(p.id);
    },
    async loadPatientPackages(patientId) {
      try {
        const r = await ApiService.get(`therapy/patients/${patientId}/packages`);
        this.patientPackages = (r.data.data || []).filter(
          (item) => item.is_usable
        );
      } catch (e) {
        this.patientPackages = [];
      }
    },
    lines(v) {
      return String(v || "")
        .split("\n")
        .map((x) => x.trim())
        .filter(Boolean);
    },
    packagePayload(item) {
      if (!item || !item.operational_payload) return {};
      if (typeof item.operational_payload === "object")
        return item.operational_payload;
      try {
        return JSON.parse(item.operational_payload) || {};
      } catch (e) {
        return {};
      }
    },
    packageSessions(item) {
      const payload = this.packagePayload(item);
      if (Array.isArray(payload.service_items))
        return payload.service_items.reduce(
          (sum, line) => sum + Number(line.sessions || 0),
          0
        );
      return Number(payload.sessions || 0);
    },
    packagePrice(item) {
      return Number(this.packagePayload(item).price || 0);
    },
    applyPackage() {
      const item = this.therapyPackages.find(
        (x) => Number(x.id) === Number(this.caseForm.package_resource_id)
      );
      if (item) {
        this.caseForm.patient_package_id = null;
        this.caseForm.authorized_sessions = this.packageSessions(item);
      }
    },
    patientPackageName(item) {
      const snapshot =
        typeof item.package_snapshot === "object" ? item.package_snapshot : {};
      return snapshot.name || `Paket #${item.id}`;
    },
    applyPatientPackage() {
      const item = this.patientPackages.find(
        (x) => Number(x.id) === Number(this.caseForm.patient_package_id)
      );
      if (item) {
        this.caseForm.package_resource_id = null;
        this.caseForm.authorized_sessions = Number(item.sessions_remaining || 0);
      }
    },
    async saveCase() {
      try {
        const payload = {
          ...this.caseForm,
          goals: this.lines(this.caseForm.goalsText),
          planned_interventions: this.lines(this.caseForm.interventionsText),
          reason: "Membuka episode terapi baru.",
        };
        delete payload.goalsText;
        delete payload.interventionsText;
        delete payload.patient_name;
        const r = await ApiService.post("therapy/cases", payload);
        this.$bvModal.hide("therapy-case");
        await this.loadCases();
        await this.openCase(r.data.data.id);
        this.toast("Episode terapi berhasil dibuat.");
      } catch (e) {
        this.fail(e);
      }
    },
    openSession() {
      this.sessionForm = {
        occurred_at: this.localDate(new Date()),
        duration_minutes: 60,
        pain_before: null,
        pain_after: null,
        subjective: "",
        objective: "",
        assessment: "",
        plan: "",
        interventionsText: "",
        payment_category_id:
          this.sessionServices.length === 1
            ? this.sessionServices[0].payment_category_id
            : null,
      };
      this.applySessionService();
      this.$bvModal.show("therapy-session");
    },
    applySessionService() {
      const item = this.sessionServices.find(
        (service) =>
          Number(service.payment_category_id) ===
          Number(this.sessionForm.payment_category_id)
      );
      if (item && item.duration_minutes)
        this.sessionForm.duration_minutes = Number(item.duration_minutes);
    },
    async saveSession() {
      try {
        const payload = {
          ...this.sessionForm,
          interventions: this.lines(this.sessionForm.interventionsText),
          reason: "Dokumentasi sesi terapi.",
        };
        delete payload.interventionsText;
        await ApiService.post(
          `therapy/cases/${this.detail.id}/sessions`,
          payload
        );
        this.$bvModal.hide("therapy-session");
        await this.refresh();
        this.activeTab = "sessions";
        this.toast("Draft sesi tersimpan.");
      } catch (e) {
        this.fail(e);
      }
    },
    async finalizeSession(s) {
      if (
        !confirm(
          `Finalisasi sesi ${s.session_number}? Setelah final, SOAP terkunci dan kuota terpakai.`
        )
      )
        return;
      try {
        await ApiService.post(`therapy/sessions/${s.id}/finalize`, {
          reason: "SOAP sesi telah diverifikasi therapist.",
        });
        await this.refresh();
        await this.loadDashboard();
        this.activeTab = "sessions";
        this.toast("Sesi final dan kuota diperbarui.");
      } catch (e) {
        this.fail(e);
      }
    },
    async sendToCashier() {
      try {
        const r = await ApiService.get(
          `therapy/cases/${this.detail.id}/billing-preview`
        );
        const preview = r.data.data;
        this.$router.push({
          name: "payment-add",
          query: {
            patient_id: preview.patient.id,
            patient_name: `${preview.patient.name} (${preview.patient.patient_id})`,
            therapy_case_id: preview.case_id,
          },
        });
      } catch (e) {
        this.fail(e);
      }
    },
    managePackages() {
      this.$router.push("/vertical-resources/therapy_session_package");
    },
    openInvoice() {
      if (!this.detail || !this.detail.billing) return;
      this.$router.push({
        name: "payment-detail",
        params: { id: this.detail.billing.id },
      });
    },
    async transition(status) {
      try {
        await ApiService.post(`therapy/cases/${this.detail.id}/transition`, {
          status,
          reason: `Perubahan status episode menjadi ${status}.`,
        });
        await this.refresh();
        await this.loadDashboard();
      } catch (e) {
        this.fail(e);
      }
    },
    discharge() {
      this.dischargeForm = {
        discharge_summary: "",
        discharge_outcome: "goals_met",
      };
      this.$bvModal.show("therapy-discharge");
    },
    async saveDischarge() {
      try {
        await ApiService.post(`therapy/cases/${this.detail.id}/transition`, {
          status: "completed",
          ...this.dischargeForm,
          reason: "Episode terapi ditutup dengan discharge.",
        });
        this.$bvModal.hide("therapy-discharge");
        await this.refresh();
        await this.loadDashboard();
        this.toast("Episode terapi berhasil ditutup.");
      } catch (e) {
        this.fail(e);
      }
    },
    async saveOutcome() {
      try {
        await ApiService.post(`therapy/cases/${this.detail.id}/outcomes`, {
          ...this.outcomeForm,
          measured_at: new Date().toISOString(),
          reason: "Pencatatan outcome terapi.",
        });
        this.$bvModal.hide("therapy-outcome");
        this.outcomeForm = { phase: "progress" };
        await this.refresh();
        this.activeTab = "outcomes";
      } catch (e) {
        this.fail(e);
      }
    },
    async saveHome() {
      try {
        await ApiService.post(`therapy/cases/${this.detail.id}/home-programs`, {
          issued_at: this.today(),
          review_at: this.homeForm.review_at || null,
          exercises: this.lines(this.homeForm.exercisesText),
          instructions: this.homeForm.instructions,
          reason: "Penerbitan home exercise program.",
        });
        this.$bvModal.hide("therapy-home");
        this.homeForm = {};
        await this.refresh();
        this.activeTab = "home";
      } catch (e) {
        this.fail(e);
      }
    },
    async refresh() {
      const id = this.detail.id;
      await this.openCase(id);
      await this.loadCases();
    },
    statusLabel(s) {
      return (
        {
          planned: "Direncanakan",
          active: "Aktif",
          on_hold: "Ditunda",
          completed: "Selesai",
          cancelled: "Dibatalkan",
        }[s] || s
      );
    },
    today() {
      return new Date().toISOString().slice(0, 10);
    },
    localDate(v) {
      const d = new Date(v);
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
    },
    date(v) {
      return v
        ? new Intl.DateTimeFormat("id-ID", { dateStyle: "medium" }).format(
            new Date(v)
          )
        : "-";
    },
    dateTime(v) {
      return v
        ? new Intl.DateTimeFormat("id-ID", {
            dateStyle: "medium",
            timeStyle: "short",
          }).format(new Date(v))
        : "-";
    },
    money(v) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
      }).format(Number(v || 0));
    },
    toast(m) {
      this.$bvToast.toast(m, {
        title: "Berhasil",
        variant: "success",
        solid: true,
      });
    },
    fail(e) {
      const d = e && e.response && e.response.data;
      this.errorMessage =
        (d && d.errors && Object.values(d.errors).flat()[0]) ||
        (d && (d.message || d.error)) ||
        "Data belum dapat diproses.";
    },
  },
};
</script>

<style scoped>
.therapy-page {
  color: #183153;
  padding-bottom: 2rem;
}
.financial-strip{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.75rem;margin:1rem 0;padding:1rem 1.2rem;border:1px solid #dbe7ec;border-left:5px solid #f59e0b;border-radius:14px;background:#fff}.financial-strip>div{display:flex;flex-direction:column}.financial-strip small{color:#64748b;font-size:.68rem}.financial-strip strong{margin-top:.2rem;color:#183153}.financial-strip.active{border-left-color:#10b981;background:#f0fdf4}.financial-strip.completed{border-left-color:#64748b}.financial-strip.not_billed,.financial-strip.unpaid{background:#fffbeb}@media(max-width:768px){.financial-strip{grid-template-columns:1fr}}
.service-balance{display:block;margin-top:.2rem;color:#475569;font-size:.68rem}.session-service{display:block;margin:.25rem 0;color:var(--skin-primary);font-weight:700}
.therapy-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding: 1.8rem 2rem;
  margin-bottom: 1rem;
  border-radius: 22px;
  color: #fff;
  background: linear-gradient(125deg, var(--skin-primary), #1d4ed8);
  box-shadow: 0 18px 40px rgba(var(--skin-primary-rgb), 0.18);
}
.therapy-hero span {
  font-size: 0.68rem;
  letter-spacing: 0.15em;
  font-weight: 800;
}
.therapy-hero h1 {
  margin: 0.3rem 0;
  font-size: 1.7rem;
}
.therapy-hero p {
  margin: 0;
  color: #dbeafe;
}
.kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.8rem;
}
.kpis article,
.case-detail,
.case-list,
.empty {
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 15px;
  background: #fff;
}
.kpis small,
.overview small {
  display: block;
  color: #64748b;
}
.kpis strong {
  font-size: 1.8rem;
}
.kpis .warning {
  border-color: #fbbf24;
  background: #fffbeb;
}
.therapy-filters {
  display: flex;
  justify-content: space-between;
  margin: 1rem 0;
  gap: 1rem;
}
.therapy-search {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  padding: 0 1rem;
  border: 1px solid #dbe7ef;
  border-radius: 12px;
  background: #fff;
}
.therapy-search input,
.therapy-filters > select {
  padding: 0.75rem;
  outline: 0;
  background: #fff;
}
.therapy-search input {
  flex: 1;
  min-width: 0;
  border: 0 !important;
  box-shadow: none !important;
}
.therapy-filters > select {
  border: 1px solid #dbe7ef;
  border-radius: 12px;
}
.layout {
  display: grid;
  grid-template-columns: 270px 1fr;
  gap: 1rem;
}
.case-list {
  align-self: start;
}
.case-list button {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0.8rem;
  margin-bottom: 0.5rem;
  border: 1px solid transparent;
  border-radius: 10px;
  background: #f8fafc;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
}
.case-list button:hover {
  border-color: #99f6e4;
  background: var(--skin-soft);
  transform: translateY(-1px);
}
.case-list button.active {
  border-color: var(--skin-secondary);
  background: var(--skin-soft);
}
.case-list span,
.case-list small,
.case-heading span {
  font-size: 0.7rem;
  color: #64748b;
}
.case-heading {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}
.case-heading h2 {
  margin: 0.2rem 0;
}
.case-heading > div:last-child {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.status {
  padding: 0.45rem 0.7rem;
  border-radius: 99px;
  background: #e2e8f0;
}
.quota {
  margin: 1rem 0;
  padding: 1rem;
  border-radius: 12px;
  background: #f8fafc;
}
.quota > div:first-child {
  display: flex;
  justify-content: space-between;
}
.quota .bar {
  height: 8px;
  margin: 0.6rem 0;
  border-radius: 99px;
  background: #dbe7ef;
  overflow: hidden;
}
.quota .bar i {
  display: block;
  height: 100%;
  background: var(--skin-secondary);
}
.quota > span {
  font-size: 0.72rem;
  color: #b45309;
}
.tabs {
  display: flex;
  gap: 0.4rem;
  border-bottom: 1px solid #e2e8f0;
}
.tabs button {
  padding: 0.7rem;
  border: 0;
  border-bottom: 3px solid transparent;
  background: none;
}
.tabs button.active {
  border-color: var(--skin-primary);
  color: var(--skin-primary);
  font-weight: 800;
}
.tabs b {
  padding: 0.15rem 0.4rem;
  border-radius: 99px;
  background: #edf2f4;
}
.grid,
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.overview {
  padding-top: 1rem;
}
.overview article {
  padding: 1rem;
  border-radius: 12px;
  background: #f8fafc;
}
.wide {
  grid-column: span 2;
}
.records {
  padding-top: 1rem;
}
.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.records article {
  margin-top: 0.7rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.records article > div {
  display: flex;
  justify-content: space-between;
}
.records article span {
  display: block;
  font-size: 0.7rem;
  color: #64748b;
}
.records article p {
  margin: 0.5rem 0;
}
.empty {
  display: grid;
  place-items: center;
  align-content: center;
  min-height: 350px;
  text-align: center;
}
.empty i {
  font-size: 3rem;
  color: var(--skin-secondary);
}
.form-grid label {
  font-size: 0.75rem;
  font-weight: 700;
}
@media (max-width: 900px) {
  .kpis {
    grid-template-columns: 1fr 1fr;
  }
  .layout {
    grid-template-columns: 1fr;
  }
  .case-list {
    display: flex;
    overflow: auto;
  }
  .case-list button {
    min-width: 210px;
  }
  .therapy-hero,
  .case-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}
@media (max-width: 520px) {
  .kpis,
  .grid,
  .form-grid {
    grid-template-columns: 1fr;
  }
  .wide {
    grid-column: auto;
  }
}
</style>
